// @ts-nocheck
/**
 * ドラフトシステム - トーナメント画面
 */

import React, { useEffect, useCallback } from 'react';

/**
 * トーナメント画面
 * @param {Object} props
 * @param {Object} props.session - ドラフトセッション
 * @param {Function} props.onStartBattle - バトル開始コールバック(playerDeck, opponentDeck, opponentName, matchInfo)
 * @param {Function} props.onTournamentEnd - トーナメント終了コールバック
 * @param {Object} props.tournamentState - トーナメント状態（DraftAppで管理）
 * @param {Function} props.setTournamentState - トーナメント状態更新関数
 */
export const DraftTournament = ({
  session,
  onStartBattle,
  onTournamentEnd,
  tournamentState,
  setTournamentState,
}) => {
  const players = session?.players || [];
  const playerCount = players.length;
  const totalRounds = Math.ceil(Math.log2(playerCount));

  // トーナメント状態を展開
  const {
    matches,
    currentRound,
    playerWins,
    playerLosses,
    isPlayerEliminated,
    tournamentComplete,
    pendingBattleResult,
    currentMatchId,
  } = tournamentState;

  // 状態更新ヘルパー
  const updateState = useCallback((updates) => {
    setTournamentState(prev => ({ ...prev, ...updates }));
  }, [setTournamentState]);

  // プレイヤーのデッキを取得
  const getPlayerDeck = useCallback(() => {
    return session?.deckBuilding?.player?.deck || [];
  }, [session]);

  // NPCのデッキを取得
  const getNPCDeck = useCallback((npcId) => {
    const npcDeckBuilding = session?.deckBuilding?.[npcId];
    return npcDeckBuilding?.deck || [];
  }, [session]);

  // 初期マッチを生成（一度だけ）
  useEffect(() => {
    if (players.length > 0 && matches.length === 0) {
      const initialMatches = [];
      // プレイヤーを含むシャッフル済みの順番でマッチを生成
      const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);

      for (let i = 0; i < shuffledPlayers.length; i += 2) {
        if (shuffledPlayers[i + 1]) {
          initialMatches.push({
            id: `match_r1_${i / 2}`,
            round: 1,
            player1: shuffledPlayers[i],
            player2: shuffledPlayers[i + 1],
            winner: null,
            isPlayerMatch: shuffledPlayers[i].id === 'player' || shuffledPlayers[i + 1].id === 'player',
          });
        }
      }
      updateState({ matches: initialMatches });
    }
  }, [players, matches.length, updateState]);

  // バトル結果を処理
  useEffect(() => {
    if (pendingBattleResult && currentMatchId) {
      console.log('[DraftTournament] Processing battle result:', pendingBattleResult, 'matchId:', currentMatchId);
      const didWin = pendingBattleResult === 'win';

      // 現在のマッチを見つける
      const currentMatch = matches.find(m => m.id === currentMatchId);
      if (!currentMatch) {
        // マッチが見つからない場合でも、バトル結果を適用してクリア
        console.warn('[DraftTournament] Match not found:', currentMatchId, 'matches:', matches);
        // matches が空の場合は次のレンダーで再試行されるので待つ
        if (matches.length === 0) {
          console.log('[DraftTournament] Waiting for matches to be initialized...');
          return;
        }
        // matches があるのに見つからない場合は状態をクリアして進める
        updateState({
          pendingBattleResult: null,
          currentMatchId: null,
          playerWins: didWin ? playerWins + 1 : playerWins,
          playerLosses: didWin ? playerLosses : playerLosses + 1,
          isPlayerEliminated: didWin ? isPlayerEliminated : true,
        });
        return;
      }

      // マッチ結果を更新
      const updatedMatches = matches.map(m => {
        if (m.id === currentMatchId) {
          const winner = didWin
            ? (m.player1.id === 'player' ? m.player1 : m.player2)
            : (m.player1.id === 'player' ? m.player2 : m.player1);
          return { ...m, winner };
        }
        return m;
      });

      console.log('[DraftTournament] Battle result processed, winner:', didWin ? 'player' : 'opponent');

      // 状態を更新
      updateState({
        matches: updatedMatches,
        playerWins: didWin ? playerWins + 1 : playerWins,
        playerLosses: didWin ? playerLosses : playerLosses + 1,
        isPlayerEliminated: didWin ? isPlayerEliminated : true,
        pendingBattleResult: null,
        currentMatchId: null,
      });
    }
  }, [pendingBattleResult, currentMatchId, matches, playerWins, playerLosses, isPlayerEliminated, updateState]);

  // 次のラウンドを生成
  const generateNextRound = useCallback(() => {
    const currentRoundMatches = matches.filter(m => m.round === currentRound);
    const winners = currentRoundMatches.map(m => m.winner).filter(Boolean);

    if (winners.length < 2) {
      // トーナメント終了
      updateState({ tournamentComplete: true });
      return;
    }

    // 次のラウンドのマッチを生成
    const nextRoundMatches = [];
    for (let i = 0; i < winners.length; i += 2) {
      if (winners[i + 1]) {
        nextRoundMatches.push({
          id: `match_r${currentRound + 1}_${i / 2}`,
          round: currentRound + 1,
          player1: winners[i],
          player2: winners[i + 1],
          winner: null,
          isPlayerMatch: winners[i].id === 'player' || winners[i + 1].id === 'player',
        });
      }
    }

    updateState({
      matches: [...matches, ...nextRoundMatches],
      currentRound: currentRound + 1,
    });
  }, [matches, currentRound, updateState]);

  // 現在のラウンドが終了したかチェック
  useEffect(() => {
    if (matches.length === 0) return;
    if (pendingBattleResult || currentMatchId) return; // バトル中は処理しない

    const currentRoundMatches = matches.filter(m => m.round === currentRound);
    const allCompleted = currentRoundMatches.every(m => m.winner !== null);

    if (allCompleted && !tournamentComplete) {
      // 次のラウンドへ
      if (currentRound < totalRounds) {
        generateNextRound();
      } else {
        updateState({ tournamentComplete: true });
      }
    }
  }, [matches, currentRound, totalRounds, tournamentComplete, pendingBattleResult, currentMatchId, generateNextRound, updateState]);

  // トーナメント終了処理
  useEffect(() => {
    if (tournamentComplete && onTournamentEnd) {
      // 順位を計算
      let placement = playerCount;
      if (!isPlayerEliminated) {
        // 最後まで勝ち残った
        const finalMatch = matches.find(m => m.round === totalRounds);
        if (finalMatch?.winner?.id === 'player') {
          placement = 1;
        } else {
          placement = 2;
        }
      } else {
        // 敗退したラウンドから順位を計算
        const eliminatedRound = matches.find(
          m => m.isPlayerMatch && m.winner && m.winner.id !== 'player'
        )?.round || 1;
        placement = Math.pow(2, totalRounds - eliminatedRound) + 1;
      }

      onTournamentEnd({
        placement,
        wins: playerWins,
        losses: playerLosses,
        totalPicks: session?.draft?.picks?.player?.length || 0,
      });
    }
  }, [tournamentComplete, isPlayerEliminated, playerWins, playerLosses, matches, totalRounds, playerCount, session, onTournamentEnd]);

  // NPC vs NPC の試合をシミュレート
  const simulateNPCMatch = useCallback((match) => {
    // ランダムに勝者を決定
    const winner = Math.random() > 0.5 ? match.player1 : match.player2;
    const updatedMatches = matches.map(m =>
      m.id === match.id ? { ...m, winner } : m
    );
    updateState({ matches: updatedMatches });
  }, [matches, updateState]);

  // プレイヤーの試合を開始
  const startPlayerMatch = useCallback((match) => {
    const playerDeck = getPlayerDeck();
    const opponent = match.player1.id === 'player' ? match.player2 : match.player1;
    const opponentDeck = getNPCDeck(opponent.id);

    onStartBattle?.(playerDeck, opponentDeck, opponent.name, {
      matchId: match.id,
      round: match.round,
    });
  }, [getPlayerDeck, getNPCDeck, onStartBattle]);

  // 次の試合を進める
  const proceedToNextMatch = useCallback(() => {
    // バトル中は何もしない
    if (currentMatchId) return;

    // 現在のラウンドで未完了の試合を探す
    const pendingMatches = matches.filter(m => m.round === currentRound && !m.winner);

    if (pendingMatches.length === 0) return;

    // プレイヤーの試合を優先
    const playerMatch = pendingMatches.find(m => m.isPlayerMatch);
    if (playerMatch && !isPlayerEliminated) {
      startPlayerMatch(playerMatch);
      return;
    }

    // NPC vs NPC の試合をシミュレート
    const npcMatch = pendingMatches.find(m => !m.isPlayerMatch);
    if (npcMatch) {
      simulateNPCMatch(npcMatch);
    }
  }, [matches, currentRound, isPlayerEliminated, currentMatchId, startPlayerMatch, simulateNPCMatch]);

  // 「次へ進む」ボタンのハンドラ
  const handleProceed = () => {
    if (currentMatchId) return;
    proceedToNextMatch();
  };

  // ラウンド名を取得
  const getRoundName = (round) => {
    if (round === totalRounds) return '決勝';
    if (round === totalRounds - 1) return '準決勝';
    return `ラウンド ${round}`;
  };

  // バトル待機中かどうか
  const waitingForBattle = !!currentMatchId;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 20px',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
    }}>
      <h1 style={{
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#ffd700',
        marginBottom: '20px',
      }}>
        ドラフトトーナメント
      </h1>

      {/* 戦績 */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '15px 30px',
        marginBottom: '20px',
        display: 'flex',
        gap: '30px',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#64c864', fontSize: '1.5rem', fontWeight: 'bold' }}>{playerWins}</div>
          <div style={{ color: '#888', fontSize: '0.9rem' }}>勝利</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#ff6b6b', fontSize: '1.5rem', fontWeight: 'bold' }}>{playerLosses}</div>
          <div style={{ color: '#888', fontSize: '0.9rem' }}>敗北</div>
        </div>
      </div>

      {/* ステータス */}
      {isPlayerEliminated && (
        <div style={{
          background: 'rgba(255, 107, 107, 0.2)',
          border: '1px solid rgba(255, 107, 107, 0.5)',
          borderRadius: '8px',
          padding: '10px 20px',
          marginBottom: '20px',
          color: '#ff6b6b',
        }}>
          敗退しました。残りの試合は自動進行します。
        </div>
      )}

      {waitingForBattle && (
        <div style={{
          background: 'rgba(255, 215, 0, 0.2)',
          border: '1px solid rgba(255, 215, 0, 0.5)',
          borderRadius: '8px',
          padding: '10px 20px',
          marginBottom: '20px',
          color: '#ffd700',
        }}>
          バトル中...
        </div>
      )}

      {/* トーナメント表 */}
      <div style={{
        display: 'flex',
        gap: '40px',
        justifyContent: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        maxWidth: '100%',
        overflowX: 'auto',
      }}>
        {Array.from({ length: totalRounds }).map((_, roundIndex) => (
          <div key={roundIndex} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            alignItems: 'center',
            minWidth: '180px',
          }}>
            <div style={{
              color: roundIndex + 1 === currentRound ? '#ffd700' : '#888',
              fontWeight: 'bold',
              marginBottom: '10px',
              fontSize: roundIndex + 1 === currentRound ? '1.1rem' : '1rem',
            }}>
              {getRoundName(roundIndex + 1)}
            </div>
            {matches
              .filter(m => m.round === roundIndex + 1)
              .map((match) => (
                <div
                  key={match.id}
                  style={{
                    background: match.winner
                      ? 'rgba(100, 200, 100, 0.1)'
                      : match.isPlayerMatch && !isPlayerEliminated
                        ? 'rgba(255, 215, 0, 0.1)'
                        : 'rgba(255, 255, 255, 0.05)',
                    border: match.winner
                      ? '1px solid rgba(100, 200, 100, 0.3)'
                      : match.isPlayerMatch && !isPlayerEliminated
                        ? '2px solid rgba(255, 215, 0, 0.5)'
                        : '1px solid #333',
                    borderRadius: '8px',
                    padding: '12px',
                    minWidth: '160px',
                  }}
                >
                  {/* Player 1 */}
                  <div style={{
                    padding: '8px',
                    marginBottom: '4px',
                    borderRadius: '4px',
                    background: match.winner?.id === match.player1.id ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                    color: match.winner?.id === match.player1.id ? '#ffd700' : '#fff',
                    fontWeight: match.player1.id === 'player' ? 'bold' : 'normal',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span>
                      {match.player1.name}
                      {match.player1.id === 'player' && ' (あなた)'}
                    </span>
                    {match.winner?.id === match.player1.id && <span>👑</span>}
                  </div>

                  <div style={{
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '0.8rem',
                    margin: '4px 0',
                  }}>
                    VS
                  </div>

                  {/* Player 2 */}
                  <div style={{
                    padding: '8px',
                    borderRadius: '4px',
                    background: match.winner?.id === match.player2.id ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                    color: match.winner?.id === match.player2.id ? '#ffd700' : '#fff',
                    fontWeight: match.player2.id === 'player' ? 'bold' : 'normal',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span>
                      {match.player2.name}
                      {match.player2.id === 'player' && ' (あなた)'}
                    </span>
                    {match.winner?.id === match.player2.id && <span>👑</span>}
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>

      {/* アクションボタン */}
      {!tournamentComplete && !waitingForBattle && (
        <button
          onClick={handleProceed}
          style={{
            padding: '15px 40px',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)',
            color: '#000',
            cursor: 'pointer',
          }}
        >
          {(() => {
            const pendingPlayerMatch = matches.find(
              m => m.round === currentRound && !m.winner && m.isPlayerMatch && !isPlayerEliminated
            );
            if (pendingPlayerMatch) {
              const opponent = pendingPlayerMatch.player1.id === 'player'
                ? pendingPlayerMatch.player2
                : pendingPlayerMatch.player1;
              return `${opponent.name} と対戦する`;
            }
            return '次の試合へ';
          })()}
        </button>
      )}
    </div>
  );
};

export default DraftTournament;
